import { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ShippingPolicy,
  CreateShippingPolicyRequest,
  DeleteShippingPolicyRequest,
  DownloadParxlLabelRequest,
  DownloadParxlLabelResponse,
} from '../../../infrastructure/ShippingClient';
import { shippingClient } from '../../../infrastructure/ShippingClient';
import type { CountryCode } from '../../../models/Currency';

export const SHIPPING_QUERY_KEYS = {
  all: ['shipping'] as const,
  policies: () => [...SHIPPING_QUERY_KEYS.all, 'policies'] as const,
};

export interface UseShippingReturn {
  // Data
  policies: ShippingPolicy[];

  // Loading states
  isLoading: boolean;
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
  isDownloadingLabels: boolean;

  // Error states
  error: Error | null;
  createError: Error | null;
  deleteError: Error | null;

  // Actions
  createOrUpdatePolicy: (policy: CreateShippingPolicyRequest) => Promise<void>;
  deletePolicy: (countryCode: CountryCode) => Promise<void>;
  refreshPolicies: () => void;
  downloadParxlLabels: (request: DownloadParxlLabelRequest) => Promise<DownloadParxlLabelResponse>;
}

export const useShipping = (): UseShippingReturn => {
  const queryClient = useQueryClient();
  const [createError, setCreateError] = useState<Error | null>(null);
  const [deleteError, setDeleteError] = useState<Error | null>(null);

  // Fetch shipping policies
  const {
    data: policies = [],
    isLoading,
    error,
    refetch: refreshPolicies,
  } = useQuery({
    queryKey: SHIPPING_QUERY_KEYS.policies(),
    queryFn: () => shippingClient.getShippingPolicies(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Create or update shipping policy mutation
  const createOrUpdateMutation = useMutation({
    mutationFn: (request: CreateShippingPolicyRequest) => shippingClient.createOrUpdateShippingPolicy(request),
    onSuccess: () => {
      setCreateError(null);
      queryClient.invalidateQueries({ queryKey: SHIPPING_QUERY_KEYS.policies() });
    },
    onError: (error: Error) => {
      setCreateError(error);
    },
  });

  // Delete shipping policy mutation
  const deleteMutation = useMutation({
    mutationFn: (request: DeleteShippingPolicyRequest) => shippingClient.deleteShippingPolicy(request),
    onSuccess: () => {
      setDeleteError(null);
      queryClient.invalidateQueries({ queryKey: SHIPPING_QUERY_KEYS.policies() });
    },
    onError: (error: Error) => {
      setDeleteError(error);
    },
  });

  // Clear errors when successful operations occur
  useEffect(() => {
    if (createOrUpdateMutation.isSuccess) {
      setCreateError(null);
    }
  }, [createOrUpdateMutation.isSuccess]);

  useEffect(() => {
    if (deleteMutation.isSuccess) {
      setDeleteError(null);
    }
  }, [deleteMutation.isSuccess]);

  const createOrUpdatePolicy = async (policy: CreateShippingPolicyRequest): Promise<void> => {
    return createOrUpdateMutation.mutateAsync(policy);
  };

  const deletePolicy = async (countryCode: CountryCode): Promise<void> => {
    return deleteMutation.mutateAsync({ countryCode });
  };

  const downloadLabelsMutation = useMutation({
    mutationFn: (request: DownloadParxlLabelRequest) => shippingClient.downloadParxlLabels(request),
  });

  const downloadParxlLabels = async (request: DownloadParxlLabelRequest): Promise<DownloadParxlLabelResponse> => {
    return downloadLabelsMutation.mutateAsync(request);
  };

  return {
    // Data
    policies,

    // Loading states
    isLoading,
    isCreating: createOrUpdateMutation.isPending,
    isUpdating: createOrUpdateMutation.isPending, // Same as creating since API uses upsert
    isDeleting: deleteMutation.isPending,
    isDownloadingLabels: downloadLabelsMutation.isPending,

    // Error states
    error,
    createError,
    deleteError,

    // Actions
    createOrUpdatePolicy,
    deletePolicy,
    refreshPolicies,
    downloadParxlLabels,
  };
};
